import { type ComponentProps, useMemo } from 'react'

import { ButtonRoot } from '@/components/common/Button'
import { FloatingPopover, useFloating } from '@/components/common/Floating'
import { InputDropdown, InputSelectWithLabel } from '@/components/common/Input'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import {
  GALLERY_SORTBY_DIRECTION,
  GALLERY_SORTBY_DIRECTION_LABELS,
  GALLERY_SORTBY_LABELS,
  type GallerySortbyDirectionType,
  type GallerySortbySlugType,
  isGallerySortbySlugsDefaultHidden,
} from '@/defs/gallery'
import { galleryActions } from '@/store/gallery'
import { useReduxDispatch } from '@/store/hooks'
import { toastActions } from '@/store/toast'
import { trackEvent } from '@/utils/analyticsUtil'
import { uniqueKeys } from '@/utils/commonUtil'
// import { scrollToAnchor } from '@/utils/scrollUtil'

import { useGalleryDisplay } from '../../GalleryDisplayProvider/GalleryDisplayProvider'
import { useGalleryPreload } from '../../GalleryPreloadProvider/GalleryPreloadProvider'
import { GalleryControlsLockable } from '../GalleryControlsLockable/GalleryControlsLockable'
import { GalleryControlsPriceAlert } from '../GalleryControlsPriceAlert/GalleryControlsPriceAlert'

import styles from './GalleryControlsSortby.module.scss'

type Props = {
  variant?: ComponentProps<typeof InputSelectWithLabel>['variant']
}

const GalleryControlsSortbyModal = () => {
  const { close } = useFloating()
  const dispatch = useReduxDispatch()
  const { sortby, sortbyDefault, sortbyDefaultIcon, sortbyDefaultLabel, sortbyDirection, sortbyOptions } =
    useGalleryDisplay()

  const onSelectSortby = (v: GallerySortbySlugType | 'reset') => {
    // TODO: (backlog) More intentional scroll target for new scrollby
    // if (v !== sortby) requestAnimationFrame(() => scrollToAnchor({ id: 'gallery', ifNearTop: true, isSmooth: true }))

    if (v === 'reset') {
      dispatch(galleryActions.setLockedPrefSlug({ isLocked: false, slug: 'groupby' }))
      dispatch(galleryActions.setSortby(sortbyDefault))
      return
    }

    if (v === 'price-preferred') {
      dispatch(toastActions.add(<GalleryControlsPriceAlert variant='sortby' />))
    }

    dispatch(galleryActions.setSortby(v))
    trackEvent({ action: 'sort_by_submit', category: 'cardlists', label: v })
    close()
  }

  const onSelectSortbyDirection = (v: GallerySortbyDirectionType) => {
    dispatch(galleryActions.setSortbyDirection(v))
  }

  const labels = {
    ...GALLERY_SORTBY_LABELS,
    default: { icon: sortbyDefaultIcon, label: sortbyDefaultLabel },
  } as const

  const optionsSortby = sortbyOptions
    .filter((slug) => !isGallerySortbySlugsDefaultHidden(slug))
    .map((slug) => ({
      label: <IconWithLabel iconBefore={labels[slug].icon}>{labels[slug].label}</IconWithLabel>,
      value: slug,
    }))

  const optionsSortbyDirection = GALLERY_SORTBY_DIRECTION.map((slug) => ({
    label: (
      <IconWithLabel iconBefore={GALLERY_SORTBY_DIRECTION_LABELS[slug].icon} wrapNone>
        {GALLERY_SORTBY_DIRECTION_LABELS[slug].label}
      </IconWithLabel>
    ),
    value: slug,
  }))

  return (
    <div className={styles.dropdown}>
      <InputDropdown
        onSelect={onSelectSortby}
        options={optionsSortby}
        reset={{ label: 'Reset', value: 'reset' }}
        value={sortby}
      />
      <InputDropdown onSelect={onSelectSortbyDirection} options={optionsSortbyDirection} value={sortbyDirection} />
    </div>
  )
}

export const GalleryControlsSortby = ({ variant }: Props) => {
  const { startPreload } = useGalleryPreload()
  const { sortby, sortbyDefaultIcon, sortbyDefaultLabel, sortbyDirection, sortbyOptions } = useGalleryDisplay()

  const { icon: iconAfter } = GALLERY_SORTBY_DIRECTION_LABELS[sortbyDirection]

  const options = useMemo(() => {
    const labels = {
      ...GALLERY_SORTBY_LABELS,
      default: { icon: sortbyDefaultIcon, label: sortbyDefaultLabel },
    } as const

    const slugs = uniqueKeys([...sortbyOptions.filter((s) => !isGallerySortbySlugsDefaultHidden(s)), sortby])

    return slugs.map((slug) => ({
      label: (
        <IconWithLabel iconAfter={iconAfter} iconAfterSize='xs' iconBefore={labels[slug].icon} wrapNone>
          {labels[slug].label}
        </IconWithLabel>
      ),
      value: slug,
    }))
  }, [iconAfter, sortby, sortbyDefaultIcon, sortbyDefaultLabel, sortbyOptions])

  return (
    <GalleryControlsLockable editedKeys={['sortby', 'sortbyDirection']} onPointerEnter={startPreload} slug='sortby'>
      <FloatingPopover content={<GalleryControlsSortbyModal />}>
        <ButtonRoot className={styles.button}>
          <InputSelectWithLabel label='Sort by' options={options} size='sm' value={sortby} variant={variant} />
        </ButtonRoot>
      </FloatingPopover>
    </GalleryControlsLockable>
  )
}
